<script setup lang="ts">
import { FairButton } from '@/ui/primitives/FairButton'
import FairDialogContent from './FairDialogContent.vue'
import FairDialogFooter from './FairDialogFooter.vue'
import FairDialogHeader from './FairDialogHeader.vue'
import type { FairIconName } from '@/ui/tokens/icons.data'

interface FairDialogPanelProps {
  header?: string
  closable?: boolean
  showHeader?: boolean
  showFooter?: boolean
  showCancel?: boolean
  cancelLabel?: string
  confirmLabel?: string
  cancelLeadingIcon?: FairIconName
  cancelTrailingIcon?: FairIconName
  confirmLeadingIcon?: FairIconName
  confirmTrailingIcon?: FairIconName
  contentText?: string
}

withDefaults(defineProps<FairDialogPanelProps>(), {
  header: 'Header',
  closable: true,
  showHeader: true,
  showFooter: true,
  showCancel: true,
  cancelLabel: 'Для себя',
  confirmLabel: 'Для себя',
  cancelLeadingIcon: undefined,
  cancelTrailingIcon: undefined,
  confirmLeadingIcon: undefined,
  confirmTrailingIcon: undefined,
  contentText: undefined,
})

const emit = defineEmits<{
  close: []
  cancel: [event: MouseEvent]
  confirm: [event: MouseEvent]
}>()
</script>

<template>
  <div
    class="fair-dialog-panel"
    role="dialog"
    aria-modal="false"
  >
    <FairButton
      v-if="closable"
      class="fair-dialog-panel__close"
      variant="flat-action"
      size="m"
      icon-only
      leading-icon="close"
      aria-label="Close"
      @click="emit('close')"
    />

    <slot name="header">
      <FairDialogHeader
        v-if="showHeader"
        :title="header"
      >
        <template
          v-if="$slots['header-start']"
          #start
        >
          <slot name="header-start" />
        </template>
        <template
          v-if="$slots['header-end']"
          #end
        >
          <slot name="header-end" />
        </template>
      </FairDialogHeader>
    </slot>

    <slot>
      <FairDialogContent
        v-if="contentText"
        :text="contentText"
      />
    </slot>

    <slot name="footer">
      <FairDialogFooter
        v-if="showFooter"
        :show-cancel="showCancel"
        :cancel-label="cancelLabel"
        :confirm-label="confirmLabel"
        :cancel-leading-icon="cancelLeadingIcon"
        :cancel-trailing-icon="cancelTrailingIcon"
        :confirm-leading-icon="confirmLeadingIcon"
        :confirm-trailing-icon="confirmTrailingIcon"
        @cancel="emit('cancel', $event)"
        @confirm="emit('confirm', $event)"
      />
    </slot>
  </div>
</template>
